<template>
  <div>
    <v-card class="my-1">
      <v-card-title>{{ auditReport }}</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-row class="px-4">
            <v-col>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDateDisplay"
                    v-validate="'required'"
                    :error-messages="errors.collect('startDateDisplay')" 
                    data-vv-name="startDateDisplay"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    data-vv-as="start date"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  type="month"
                  :allowed-dates="allowedMonths"
                  @input="startDateChange"
                />
              </v-menu>
            </v-col>

            <v-col>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDateDisplay"
                    v-validate="'required'"
                    :error-messages="errors.collect('endDateDisplay')" 
                    data-vv-name="endDateDisplay"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    data-vv-as="end date"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  type="month"
                  :allowed-dates="allowedMonths"
                  :min="startDate"
                  @input="endDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedGroups"
                :items="groupOptions"
                label="Company Groups"
                item-text="text"
                item-value="value"
                multiple
                chips
                small-chips
                deletable-chips
                hide-selected
                clearable
              />
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                :loading="loading"
                label="Companies"
                item-text="name"
                item-value="id"
                :items="companyOptions"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                :error-messages="errors.collect('selectedCompanies')"
                data-vv-name="selectedCompanies"  
                data-vv-as="companies"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="progressing" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>
      
          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="progressing"
                @click="submitForm"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>  
      </v-row>  
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      auditReport: {
        type: String,
        required: true,
      },
      payrollPeriods: {
        type: Array,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        progressing: false,
        startDateMenu: false,
        startDate: null,
        endDateMenu: false,
        endDate: null,
        selectedGroups: [],
        selectedCompanies: [],
        progressbar: 0,
      }
    },
    computed: {
      allowedMonths () {
        const payrollMonths = this.payrollPeriods.map((period) =>
          period.payroll_date.slice(0, 7)
        )
        return (date) => payrollMonths.includes(date.slice(0, 7))
      },
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('MMM YYYY') : ''
      },
      filteredPayrollPeriods () {
        if (!this.startDate || !this.endDate) return []

        const start = new Date(this.startDate)
        const end = new Date(this.endDate)

        return this.payrollPeriods
          .filter((period) => {
            const payrollDate = new Date(period.payroll_date)
            return payrollDate >= start && payrollDate <= end
          })
      },
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },
    },
    watch: {
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    created () {
      this.loading = true

      if (this.payrollPeriods.length > 0) {
        this.startDate = this.payrollPeriods[0].payroll_date.slice(0, 7)
        this.endDate = this.payrollPeriods[0].payroll_date.slice(0, 7)
      }
      
      this.loading = false
    },
    methods: {
      startDateChange () {
        this.startDateMenu = false
        this.endDateMenu = false
        this.endDate = this.$moment(this.startDate).endOf('month').format('YYYY-MM')
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true

            let auditForm = 'epfFormA'

            if (this.auditReport === 'SOCSO Form 8A') {
              auditForm = 'socsoForm8a'
            } else if (this.auditReport === 'EIS Lampiran 1') {
              auditForm = 'eisLampiranOne'
            }

            this.$api.payrollReports()[auditForm](this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let blob = new Blob([res.data], { type: 'application/zip' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', this.auditReport + ' [' + this.startDateDisplay + ' - ' + this.endDateDisplay + '].zip') 
                link.click()
                this.progressing = false
                this.progressbar = 0

                this.$snack({ message: this.auditReport + ' downloaded successfully' })
              })
              .catch(error => {
                this.progressing = false
                if (error.response.status === 400) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.status, 
                  })
                } else if (error.response.status === 404) {
                  this.$snack({ color: 'error', message: 'No record found' })
                }
              })
          })
      },
      prepareFormData () {
        let payrollCycleTransactionIds = []
        
        this.filteredPayrollPeriods.forEach(payrollPeriod => {
          payrollPeriod.payroll_cycle_transactions.forEach(payrollCycleTransaction => {
            payrollCycleTransactionIds.push(payrollCycleTransaction.id)
          })
        })

        const sortedPayrollCycleTransactionIds = payrollCycleTransactionIds.sort((a, b) => a - b)

        return {
          payrollCycleTransactionIds: sortedPayrollCycleTransactionIds,
          companyUids: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies } 
      },
    },
  }
</script>
